import AvatarCompany from "@/components/company/avatar-company";
import CompanyActivityBox from "@/components/company/shared/company-activity-box";
import CompanyExternalLinks from "@/components/company/shared/company-external-links";
import CompanyPanelHeader from "@/components/company/shared/company-panel-header";
import RowsCard from "@/components/company/shared/rows-card";
import { ENRICHMENT_GRADIENT_STYLE } from "@/components/company/shared/enrichment-gradient";
import CompanyLegalInfoSection from "@/components/company/company-legal-info-section";
import CompanyListingsSelector from "@/components/company/company-listings-selector";
import CompanyTechnologiesSection from "@/components/company/company-technologies-section";
import CompanyUserAssignedSelector from "@/components/company/company-user-assigned-selector";
import CopyableField from "@/components/company/copyable-field";
import {
  pickEmployeesValue,
  pickFoundedYear,
  pickFundingSummary,
  pickLocation,
  pickRevenueValue,
  pickWebTrafficRows,
} from "@/components/company/shared/company-side-panel-helpers";
import KeyValueRow from "@/components/company/shared/key-value-row";
import PanelActionsBar from "@/components/company/shared/panel-actions-bar";
import SectionHeaderWithIcon from "@/components/company/shared/section-header-with-icon";
import StatTile from "@/components/company/shared/stat-tile";
import ToggleableBadgeList from "@/components/company/shared/toggleable-badge-list";
import TopExecutiveSection from "@/components/company/top-executive-section";
import CompanyIntegrationsSync from "@/components/integrations/company/company-integrations-sync";
import { Badge } from "@/components/ui/badge";
import { buttonVariants } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import useAccess from "@/hooks/useAccess";
import { useConnectedCrmIntegrations } from "@/hooks/useConnectedCrmIntegrations";
import { useActivityTypeTranslation } from "@/hooks/useActivityTypeTranslation";
import { useCompanyEnrichment } from "@/hooks/useCompanyEnrichment";
import useCopyToClipboard from "@/hooks/useCopyToClipboard";
import { useTranslation } from "@/hooks/useTranslation";
import {
  buildCompanyBusinessLinks,
  buildCompanySocialLinks,
  CompanyLink,
} from "@/lib/company-social-links";
import {
  KEY_FIGURE_TILES,
  KEY_FIGURES_GRID_CLASS,
  type KeyFigureId,
} from "@/lib/company-web-figures";
import { companyRefusalKey } from "@/lib/company-refusal";
import { internalLinkTarget } from "@/lib/internal-link-target";
import { cn } from "@/lib/utils";
import { companyPreviewPanelStore } from "@/stores/company-preview-panel-store";
import { CompanyDTO } from "@shaarp/common";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRightIcon,
  CheckIcon,
  LineChartIcon,
  Loader2Icon,
  MailIcon,
  PhoneIcon,
  SearchIcon,
  SparklesIcon,
  SwordsIcon,
} from "lucide-react";
import {
  Fragment,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { RiGlobalLine } from "react-icons/ri";
import { NavLink, useLocation } from "react-router-dom";

const COMPETITORS_LIMIT = 8;

const COLLAPSED_LINE_COUNT = 4;

function ExpandableText({ text }: { text: string }) {
  const { t } = useTranslation();
  const textRef = useRef<HTMLParagraphElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [collapsedHeight, setCollapsedHeight] = useState<number | null>(null);
  const [fullHeight, setFullHeight] = useState<number | null>(null);
  const hasUserToggledRef = useRef(false);

  useLayoutEffect(() => {
    const element = textRef.current;
    if (!element) return;

    const measure = () => {
      const full = element.scrollHeight;
      const styles = window.getComputedStyle(element);
      const parsedLineHeight = parseFloat(styles.lineHeight);
      const lineHeight = Number.isFinite(parsedLineHeight)
        ? parsedLineHeight
        : parseFloat(styles.fontSize) * 1.5;
      const collapsed = Math.min(lineHeight * COLLAPSED_LINE_COUNT, full);
      setFullHeight(full);
      setCollapsedHeight(collapsed);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [text]);

  const isOverflowing =
    fullHeight != null &&
    collapsedHeight != null &&
    fullHeight > collapsedHeight + 1;
  const targetHeight = isExpanded || !isOverflowing ? fullHeight : collapsedHeight;

  return (
    <>
      <motion.div
        initial={false}
        animate={{ height: targetHeight ?? 0 }}
        transition={
          hasUserToggledRef.current
            ? { duration: 0.3, ease: "easeOut" }
            : { duration: 0 }
        }
        style={{ overflow: "hidden" }}
      >
        <p
          ref={textRef}
          className="text-sm text-foreground/90 whitespace-pre-line"
        >
          {text}
        </p>
      </motion.div>
      {isOverflowing && (
        <button
          type="button"
          onClick={() => {
            hasUserToggledRef.current = true;
            setIsExpanded((prev) => !prev);
          }}
          className="self-start text-xs font-medium cursor-pointer text-majorelle hover:underline active:brightness-75 transition"
        >
          {isExpanded ? t("common.seeLess") : t("common.seeMore")}
        </button>
      )}
    </>
  );
}

type BannerStage = "visible" | "fading" | "hidden";

const DISMISSED_ENRICHMENT_BANNERS_KEY = "shaarp:dismissed-enrichment-banners";

function readDismissedEnrichmentBanners(): string[] {
  try {
    const stored = window.sessionStorage.getItem(
      DISMISSED_ENRICHMENT_BANNERS_KEY,
    );
    if (!stored) return [];
    const parsed: unknown = JSON.parse(stored);
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === "string")
      : [];
  } catch {
    return [];
  }
}

function isEnrichmentBannerDismissed(companyId: string): boolean {
  return readDismissedEnrichmentBanners().includes(companyId);
}

function markEnrichmentBannerDismissed(companyId: string): void {
  try {
    const ids = readDismissedEnrichmentBanners();
    if (ids.includes(companyId)) return;
    window.sessionStorage.setItem(
      DISMISSED_ENRICHMENT_BANNERS_KEY,
      JSON.stringify([...ids, companyId]),
    );
  } catch {
    // sessionStorage may be unavailable (private mode, quota exceeded)
  }
}

/**
 * Why an enrichment run ended short of enriching, as a translation key: the
 * spent daily budget, the refusal or the failure; null when it did not.
 */
function enrichmentShortfallKey(
  budgetSpent: boolean,
  error: unknown,
): string | null {
  if (budgetSpent) return "company.alerts.budgetSpent";
  if (error) return companyRefusalKey(error) ?? "company.sidePanel.enrich.failed";
  return null;
}

export function CompanyEnrichmentBanner({
  company,
  hideFadeOverlay,
}: {
  company: CompanyDTO;
  hideFadeOverlay?: boolean;
}) {
  const { t } = useTranslation();
  const { isActive, hasCompleted, budgetSpent, error, progress, start } =
    useCompanyEnrichment(company.id);
  const currentLevel = company.enrichmentLevel ?? 0;
  const [stage, setStage] = useState<BannerStage>(() =>
    isEnrichmentBannerDismissed(company.id) ? "hidden" : "visible",
  );
  const observedActiveRef = useRef(isActive);

  useEffect(() => {
    if (isActive) observedActiveRef.current = true;
  }, [isActive]);

  const hasWatchedRunEnded = !isActive && observedActiveRef.current;
  const showSuccess = hasCompleted && hasWatchedRunEnded;
  // A run this banner watched that ended short of enriching says why instead
  // of a silent button.
  const shortfallKey = hasWatchedRunEnded
    ? enrichmentShortfallKey(budgetSpent, error)
    : null;
  const shortfallMessage = shortfallKey && t(shortfallKey);

  useEffect(() => {
    if (showSuccess && stage === "visible") {
      const fadeTimer = setTimeout(() => setStage("fading"), 1200);
      return () => clearTimeout(fadeTimer);
    }
  }, [showSuccess, stage]);

  useEffect(() => {
    if (stage === "fading") {
      const hideTimer = setTimeout(() => {
        markEnrichmentBannerDismissed(company.id);
        setStage("hidden");
      }, 500);
      return () => clearTimeout(hideTimer);
    }
  }, [stage, company.id]);

  if (stage === "hidden") return null;
  if (currentLevel >= 4 && !isActive && !showSuccess && !shortfallMessage) {
    return null;
  }

  const showProgress = isActive || showSuccess;
  const isFading = stage === "fading";

  return (
    <div className="relative">
      {!hideFadeOverlay && (
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute bottom-full inset-x-0 h-20 bg-gradient-to-t from-background to-transparent transition-opacity duration-500",
            isFading && "opacity-0",
          )}
        />
      )}
      <div
        className={cn(
          "grid transition-all duration-500 ease-out",
          isFading
            ? "grid-rows-[0fr] opacity-0 pointer-events-none"
            : "grid-rows-[1fr] opacity-100",
        )}
      >
        <div className="overflow-hidden">
          <div className="bg-background">
            <div className="px-4 pt-2 pb-3">
            <button
              type="button"
              onClick={start}
              disabled={isActive || showSuccess}
              aria-label={t("company.sidePanel.enrich.label")}
              style={ENRICHMENT_GRADIENT_STYLE}
              className="w-full flex items-center gap-3 rounded-xl p-3 text-left text-white shadow-md transition-opacity hover:opacity-95 disabled:cursor-default disabled:opacity-100"
            >
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/15 text-white shrink-0">
                <SparklesIcon className="size-4" />
              </span>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold leading-tight">
                  {t("company.sidePanel.enrich.title")}
                </h3>
                <AnimatePresence initial={false} mode="wait">
                  {showProgress ? (
                    <motion.div
                      key="progress"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                      className="overflow-hidden"
                    >
                      <Progress
                        value={showSuccess ? 100 : progress}
                        className="mt-2 h-1.5 bg-white/20 [&_[data-slot=progress-indicator]]:bg-white [&_[data-slot=progress-indicator]]:bg-none"
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="description"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: "easeOut" }}
                      className="overflow-hidden"
                    >
                      <p
                        role={shortfallMessage ? "status" : undefined}
                        className="mt-0.5 text-xs text-white/80 leading-snug"
                      >
                        {shortfallMessage ??
                          t("company.sidePanel.enrich.description")}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <span
                className={cn(
                  "inline-flex h-8 w-8 items-center justify-center rounded-full shrink-0 transition-colors",
                  showSuccess
                    ? "bg-emerald-500 text-white"
                    : "bg-white text-majorelle",
                )}
              >
                {showSuccess ? (
                  <CheckIcon className="size-4" />
                ) : isActive ? (
                  <Loader2Icon className="size-4 animate-spin" />
                ) : (
                  <ArrowRightIcon className="size-4" />
                )}
              </span>
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}

export function CompanySidePanelBody({
  company,
  headerActions,
  headerContactCtas,
  headerSummary,
  hidePhoneAndEmailRows = false,
  hideLegalInfoSection = false,
  hideTechnologiesSection = false,
  webFigures,
  topExecutiveSlot,
  children,
  bodyFooter,
  headerClassName = "bg-gradient-to-br from-primary/5 to-transparent px-6 pt-8 pb-6 compact:px-4 compact:pt-5 compact:pb-4 border-b border-border",
  bodyClassName = "p-6 space-y-6 compact:p-4 compact:space-y-4",
}: {
  company: CompanyDTO;
  headerActions?: ReactNode;
  headerContactCtas?: ReactNode;
  /** A summary from elsewhere (the web synthesis), under the base's activity. */
  headerSummary?: ReactNode;
  hidePhoneAndEmailRows?: boolean;
  hideLegalInfoSection?: boolean;
  hideTechnologiesSection?: boolean;
  /** A tile from elsewhere (the web profile) for each key figure the base lacks. */
  webFigures?: Partial<Record<KeyFigureId, ReactNode>>;
  topExecutiveSlot?: ReactNode;
  /**
   * Inside the body, under the contact, above the base's own details: legal
   * information, web traffic, stack, funding, competitors.
   */
  children?: ReactNode;
  /** The last line of the body, under every section. */
  bodyFooter?: ReactNode;
  headerClassName?: string;
  bodyClassName?: string;
}) {
  const { t } = useTranslation();
  const { translateActivityType } = useActivityTypeTranslation();
  const [copiedValue, copy] = useCopyToClipboard();
  const { latestData } = useCompanyEnrichment(company.id);
  const displayedCompany = latestData ?? company;

  const aboutText =
    displayedCompany.activityMain || displayedCompany.websiteResume;
  const phone = displayedCompany.contact?.phones?.[0]?.phone;
  const email = displayedCompany.contact?.emails?.[0]?.email;
  const websiteUrl = displayedCompany.website;
  const activityTypeLabel = displayedCompany.activityTypeMain
    ? translateActivityType(displayedCompany.activityTypeMain)
    : null;

  const revenue = pickRevenueValue(displayedCompany);
  const employees = pickEmployeesValue(displayedCompany);
  const founded = pickFoundedYear(displayedCompany);
  const location = pickLocation(displayedCompany);

  const webTrafficRows = pickWebTrafficRows(displayedCompany, t);
  const fundingSummary = pickFundingSummary(displayedCompany);
  const competitors = displayedCompany.competitors ?? [];

  const businessLinks = buildCompanyBusinessLinks(displayedCompany);
  const socialLinks = buildCompanySocialLinks(displayedCompany);
  const websiteAsLink: CompanyLink | null = websiteUrl
    ? {
        icon: RiGlobalLine,
        label: t("components.dataTableCompanies.links.website", {
          domain: displayedCompany.domain ?? websiteUrl,
        }),
        url: websiteUrl,
        colorClass: "",
      }
    : null;
  const externalLinks = [
    ...(websiteAsLink ? [websiteAsLink] : []),
    ...socialLinks,
    ...businessLinks,
  ];

  // The base's figures win; the ones it lacks come from `webFigures`.
  const baseFigures: Record<KeyFigureId, string | null> = {
    revenue,
    headcount: employees,
    founded,
    location,
  };
  const figureTiles = KEY_FIGURE_TILES.flatMap(({ id, icon, tone, labelKey }) => {
    const value = baseFigures[id];
    const tile = value ? (
      <StatTile icon={icon} tone={tone} label={t(labelKey)} value={value} />
    ) : (
      webFigures?.[id]
    );
    return tile ? [{ id, tile }] : [];
  });

  return (
    <div>
      <CompanyPanelHeader
        className={headerClassName}
        avatar={<AvatarCompany className="w-14 h-14" company={displayedCompany} />}
        name={displayedCompany.name}
        actions={headerActions}
      >
        {headerContactCtas && (
          <div className="mt-4">{headerContactCtas}</div>
        )}
        {aboutText && (
          <CompanyActivityBox
            title={t("company.insights.activity")}
            badge={
              activityTypeLabel && (
                <Badge variant="secondary">{activityTypeLabel}</Badge>
              )
            }
          >
            <ExpandableText text={aboutText} />
          </CompanyActivityBox>
        )}
        {headerSummary}
        <CompanyExternalLinks links={externalLinks} />
      </CompanyPanelHeader>

      <div className={bodyClassName}>
        {figureTiles.length > 0 && (
          <div className={KEY_FIGURES_GRID_CLASS}>
            {figureTiles.map(({ id, tile }) => (
              <Fragment key={id}>{tile}</Fragment>
            ))}
          </div>
        )}

        {topExecutiveSlot !== undefined ? (
          topExecutiveSlot
        ) : (
          <TopExecutiveSection company={displayedCompany} />
        )}

        {!hidePhoneAndEmailRows && (phone || email) && (
          <section>
            <div className="flex flex-col gap-2">
              {phone && (
                <CopyableField
                  icon={PhoneIcon}
                  label={t("company.insights.keyInfo.phone")}
                  value={phone}
                  copiedValue={copiedValue}
                  onCopy={copy}
                />
              )}
              {email && (
                <CopyableField
                  icon={MailIcon}
                  label={t("company.insights.keyInfo.email")}
                  value={email}
                  copiedValue={copiedValue}
                  onCopy={copy}
                  href={`mailto:${email}`}
                />
              )}
            </div>
          </section>
        )}

        {children}

        {!hideLegalInfoSection && (
          <CompanyLegalInfoSection company={displayedCompany} />
        )}

        {webTrafficRows.length > 0 && (
          <section>
            <SectionHeaderWithIcon
              icon={LineChartIcon}
              title={t("company.insights.digital.webTraffic")}
            />
            <RowsCard>
              {webTrafficRows.map((row) => (
                <KeyValueRow key={row.label} label={row.label}>
                  {row.value}
                </KeyValueRow>
              ))}
            </RowsCard>
          </section>
        )}

        {!hideTechnologiesSection && (
          <CompanyTechnologiesSection company={displayedCompany} />
        )}

        {fundingSummary && (
          <section>
            <SectionHeaderWithIcon
              title={t("company.insights.funding.title")}
            />
            <RowsCard>
              {fundingSummary.totalFundingFormatted && (
                <KeyValueRow label={t("company.insights.funding.totalRaised")}>
                  {fundingSummary.totalFundingFormatted}
                </KeyValueRow>
              )}
              {fundingSummary.latestRoundLabel && (
                <KeyValueRow label={t("company.insights.funding.latestRound")}>
                  {fundingSummary.latestRoundLabel}
                </KeyValueRow>
              )}
              {fundingSummary.totalRoundsCount != null && (
                <KeyValueRow label={t("company.insights.funding.roundsCount")}>
                  {fundingSummary.totalRoundsCount}
                </KeyValueRow>
              )}
            </RowsCard>
          </section>
        )}

        {competitors.length > 0 && (
          <section>
            <SectionHeaderWithIcon
              icon={SwordsIcon}
              title={t("company.insights.competitors.title")}
            />
            <ToggleableBadgeList
              items={competitors}
              initialDisplay={COMPETITORS_LIMIT}
              getKey={(competitor) => competitor}
            />
          </section>
        )}

        {bodyFooter}
      </div>
    </div>
  );
}

/** A menu of the panel's actions bar. */
export type CompanyPanelMenu = "listings" | "crm" | "assignee";

/**
 * The actions bar of a company panel: lists, CRM, full page, assignee.
 * `openMenu` opens one of its menus as it mounts — the panel of a company
 * saved by the click on that menu.
 */
export function CompanyPanelFooter({
  company,
  openMenu,
}: {
  company: CompanyDTO;
  openMenu?: CompanyPanelMenu;
}) {
  const { t } = useTranslation();
  const connectedCrmIntegrations = useConnectedCrmIntegrations();
  const { pathname } = useLocation();
  const fullPagePath = `/company/${company.id}`;
  // The company page is a professional seat's: offered to a basic seat, it
  // would bounce back to the home screen and lose the conversation.
  const { hasAccess } = useAccess();
  const canOpenFullPage = hasAccess({ requiredLicences: ["professional"] });

  return (
    <PanelActionsBar>
      <CompanyListingsSelector
        company={company}
        compact
        defaultOpen={openMenu === "listings"}
      />
      {connectedCrmIntegrations.length > 0 && (
        <CompanyIntegrationsSync
          company={company}
          defaultOpen={openMenu === "crm"}
        />
      )}
      {canOpenFullPage && (
        <NavLink
          to={fullPagePath}
          target={internalLinkTarget()}
          // Opened in place, the page would load under the full-screen panel,
          // which outlives routes: put it away first. Already on that page,
          // closing is all there is to do — a link to the current path
          // replaces the entry the panel's back gesture owns.
          onClick={(event) => {
            if (internalLinkTarget()) return;
            if (pathname === fullPagePath) event.preventDefault();
            companyPreviewPanelStore.close();
          }}
          className={cn(buttonVariants({ variant: "default" }), "flex-1 gap-1.5")}
        >
          <SearchIcon className="size-4" />
          {t("components.dataTableCompanies.columns.sidePanelViewFullPage")}
        </NavLink>
      )}
      <CompanyUserAssignedSelector
        company={company}
        defaultOpen={openMenu === "assignee"}
      />
    </PanelActionsBar>
  );
}

export default function CompanyPreviewPanelContent({
  company,
}: {
  company: CompanyDTO;
}) {
  const { latestData } = useCompanyEnrichment(company.id);
  const displayedCompany = latestData ?? company;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto overscroll-y-contain">
        <CompanySidePanelBody company={displayedCompany} />
      </div>

      <CompanyEnrichmentBanner
        key={displayedCompany.id}
        company={displayedCompany}
      />

      <CompanyPanelFooter company={displayedCompany} />
    </div>
  );
}
